// @givmo/assistant-panel — the organization's consent state, and the act of binding it.
//
// ══ THE THREE STATES THIS SURFACE MUST TELL APART ══════════════════════════════════════════════
//
//   available + accepted at the CURRENT version  → the assistant answers; a first-time admin sees
//                                                   the once-per-person notice
//   available + not accepted (or an OLD version,  → a representative sees the acceptance card; anyone
//               or revoked)                         else sees one honest line and no control
//   UNAVAILABLE (the read 404s)                   → this environment serves no instrument at all
//
// The third is not an error and is the EXPECTED state on landing: no class has a seeded instrument
// yet, because the copy is signed separately and a seed migration lights the path up with no code
// change. It has to render as a calm answer, which is why `isConsentInstrumentUnavailable` classifies
// on status alone and nothing here retries a 404.
//
// ⚠ THE READ IS NOT THE GATE. `current_version_accepted` is exactly the SERVER's turn-gate predicate,
// so this client can anticipate a refusal — but the authority is the send's `428`, which re-reads the
// ledger with no cache in between. That matters because a revocation takes effect on the very next
// turn: a panel that trusted a minutes-old read would keep sending into a refusal. So the panel
// SENDS, and a 428 surfaces the card with the state the refusal itself carried.

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { decodeOrgConsent, type OrgConsentRow } from './assistant-decode';
import {
  acceptOrgConsent,
  fetchOrgConsent,
  isConsentInstrumentUnavailable,
} from './assistant-transport';

/** This package's cache keys. Namespaced so a host can never collide with them by accident. */
export const assistantPanelKeys = {
  all: ['assistant-panel'] as const,
  orgConsent: (orgRef: string) => ['assistant-panel', 'org-consent', orgRef] as const,
  /** One thread's reads. `match` (a thread scoped to entry-context ids, the email designer's) keys a
   *  thread apart from another of the same surface; absent, the key is the surface's alone. */
  thread: (wireSurface: string, match?: string) =>
    match === undefined || match.length === 0
      ? (['assistant-panel', 'thread', wireSurface] as const)
      : (['assistant-panel', 'thread', wireSurface, match] as const),
} as const;

/** What the panel needs to know about consent, already reduced from the read. */
export interface AssistantConsentGate {
  /** The instrument, when this environment serves one. */
  instrument: OrgConsentRow | null;
  /** The organization has accepted the CURRENT version — the turn gate's own predicate. */
  satisfied: boolean;
  /** No instrument is served here (the 404). Not an error; there is nothing to accept. */
  unavailable: boolean;
  /** The read has not settled yet. */
  pending: boolean;
  /** The read failed for a reason that is NOT absence — a live problem, not a dark surface. */
  errored: boolean;
}

/**
 * Read the organization's consent state.
 *
 * `retry: false` is deliberate on both legs: a 404 is an ANSWER (this environment has no instrument)
 * and retrying it spends requests to re-learn the same thing, while a genuine failure is surfaced to
 * the panel rather than hidden behind a retry loop the person cannot see. `staleTime` is zero
 * because a revocation must be visible on the next open, not after a cache window.
 */
export function useOrgAssistantConsent(orgRef: string | null): UseQueryResult<unknown> {
  return useQuery({
    queryKey: assistantPanelKeys.orgConsent(orgRef ?? ''),
    queryFn: ({ signal }) => fetchOrgConsent(orgRef as string, { signal }),
    enabled: orgRef !== null && orgRef.length > 0,
    retry: false,
    staleTime: 0,
  });
}

/** Reduce the read to the four facts the panel renders from. */
export function useAssistantConsentGate(orgRef: string | null): AssistantConsentGate {
  const read = useOrgAssistantConsent(orgRef);
  return useMemo(() => {
    if (orgRef === null || orgRef.length === 0) {
      return {
        instrument: null,
        satisfied: false,
        unavailable: false,
        pending: true,
        errored: false,
      };
    }
    if (read.isPending) {
      return {
        instrument: null,
        satisfied: false,
        unavailable: false,
        pending: true,
        errored: false,
      };
    }
    if (read.isError) {
      const unavailable = isConsentInstrumentUnavailable(read.error);
      return {
        instrument: null,
        satisfied: false,
        unavailable,
        pending: false,
        errored: !unavailable,
      };
    }
    const instrument = decodeOrgConsent(read.data);
    return {
      instrument,
      satisfied: instrument?.currentVersionAccepted === true,
      // A 200 whose body does not decode is treated as no instrument rather than as a failure: the
      // honest thing to say is that there is nothing to show, and there is nothing to retry.
      unavailable: instrument === null,
      pending: false,
      errored: false,
    };
  }, [orgRef, read.isPending, read.isError, read.error, read.data]);
}

/**
 * Bind the organization to the instrument.
 *
 * The hash is the sha256 of the bytes THIS client displayed, so the mutation takes it from the
 * instrument the card rendered rather than re-reading — a hash fetched separately from the body it
 * describes would defeat the whole check. A `409` means the copy moved under the open card; the
 * invalidation below re-reads it and the card re-renders on the new bytes.
 */
export function useAcceptOrgAssistantConsent(orgRef: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contentHash: string) => acceptOrgConsent(orgRef as string, contentHash),
    onSuccess: async (data) => {
      if (orgRef === null) return;
      // The write answers the re-resolved state, so seed it rather than spending a round-trip; the
      // invalidate then reconciles against the server at the next observation.
      queryClient.setQueryData(assistantPanelKeys.orgConsent(orgRef), data);
      await queryClient.invalidateQueries({ queryKey: assistantPanelKeys.orgConsent(orgRef) });
    },
    onError: async () => {
      if (orgRef === null) return;
      await queryClient.invalidateQueries({ queryKey: assistantPanelKeys.orgConsent(orgRef) });
    },
  });
}

/**
 * Whether THIS person has already seen the once-per-first-use notice.
 *
 * Per-person client state by design: the backend records the ORGANIZATION's acceptance, and whether
 * one admin has read the notice is not an organization fact. Every access is guarded — a private
 * window, a browser blocking site data, or a storage quota all throw rather than return, and none of
 * them is a reason to fail to render. An unreadable store means the notice shows again, which is the
 * safe direction for a disclosure.
 */
export function readNoticeDismissed(storageKey: string | null): boolean {
  if (storageKey === null || storageKey.length === 0) return false;
  try {
    return globalThis.localStorage?.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
}

export function useDismissNotice(storageKey: string | null): () => void {
  return useCallback(() => {
    if (storageKey === null || storageKey.length === 0) return;
    try {
      globalThis.localStorage?.setItem(storageKey, 'true');
    } catch {
      // A store that refuses the write costs the person one extra notice next time. Nothing else
      // depends on it, and failing the interaction over it would be worse.
    }
  }, [storageKey]);
}
